import './account.scss';

import { initNavbar, initFooterYear } from '@/ui';
import { initPostHog } from '@/services/posthog';
import {
  initGoogleSignIn,
  isLoggedIn,
  getStoredToken,
  getStoredUser,
  signOut,
  startYouTubeAuth,
  extractYouTubeTokensFromUrl,
} from '@/services/auth';
import { register, getProfile, getSubscription, refreshPasskey, cancelSubscription, ApiError } from '@/services/api';
import { initPaddle, openCheckout } from '@/services/paddle';
import { initBrandingPanel, renderBrandingPanel } from './branding';
import { Config } from '@/config';
import type { GoogleUser, BackendUser, PaddleCheckoutEvent, SubscriptionInfo } from '@/types';
import { initLocalMode } from '@/services/localMode';

initLocalMode();

initPostHog();

// ─── DOM refs ───
const authCard           = document.getElementById('auth-card')!;
const registerCard       = document.getElementById('register-card')!;
const dashboard          = document.getElementById('dashboard')!;
const authError          = document.getElementById('auth-error')!;
const authLoading        = document.getElementById('auth-loading')!;
const googleBtnContainer = document.getElementById('google-btn-container')!;

// Register card refs
const registerAvatar   = document.getElementById('register-avatar') as HTMLImageElement;
const registerName     = document.getElementById('register-name')!;
const registerEmail    = document.getElementById('register-email')!;
const youtubeAuthBtn   = document.getElementById('youtube-auth-btn')!;
const registerCancelBtn = document.getElementById('register-cancel-btn')!;
const registerError    = document.getElementById('register-error')!;
const registerLoading  = document.getElementById('register-loading')!;

// Dashboard refs
const userAvatar        = document.getElementById('user-avatar') as HTMLImageElement;
const userName          = document.getElementById('user-name')!;
const userEmail         = document.getElementById('user-email')!;
const passkeyVal        = document.getElementById('passkey-value')!;
const copyBtn           = document.getElementById('copy-btn')!;
const passkeyBox        = document.getElementById('passkey-box')!;
const passkeyPrompt     = document.getElementById('passkey-prompt')!;
const userSub           = document.getElementById('user-sub')!;
const userSince         = document.getElementById('user-since')!;
const refreshBtn        = document.getElementById('refresh-btn') as HTMLButtonElement;
const logoutBtn         = document.getElementById('logout-btn')!;
const statusEl          = document.getElementById('dashboard-status')!;

// Subscription dropdown refs
const subscriptionToggle    = document.getElementById('subscription-toggle')!;
const subscriptionDropdown  = document.getElementById('subscription-dropdown')!;
const currentPlanBadge      = document.getElementById('current-plan-badge')!;
const subscriptionStatus    = document.getElementById('subscription-status')!;
const subscriptionStatusTitle = document.getElementById('subscription-status-title')!;
const subscriptionStatusDesc  = document.getElementById('subscription-status-desc')!;
const starterBadge          = document.getElementById('starter-badge')!;
const unlimitedBadge        = document.getElementById('unlimited-badge')!;
const checkoutStarterBtn    = document.getElementById('checkout-starter-btn') as HTMLButtonElement;
const checkoutUnlimitedBtn  = document.getElementById('checkout-unlimited-btn') as HTMLButtonElement;
const planCardStarter       = document.getElementById('plan-card-starter')!;
const planCardUnlimited     = document.getElementById('plan-card-unlimited')!;
const cancelSection         = document.getElementById('cancel-section')!;
const cancelBtn             = document.getElementById('cancel-btn') as HTMLButtonElement;
const resubscribeSection    = document.getElementById('resubscribe-section')!;
const resubscribeBtn        = document.getElementById('resubscribe-btn') as HTMLButtonElement;

// ─── State ───
let currentPasskey = '';
let currentBackendUser: BackendUser | null = null;

// ─── View helpers ───

function showView(view: 'auth' | 'register' | 'dashboard'): void {
  authCard.style.display     = view === 'auth'      ? '' : 'none';
  registerCard.style.display = view === 'register'  ? '' : 'none';
  dashboard.style.display    = view === 'dashboard'  ? '' : 'none';
}

let statusTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * The account page's one place for feedback: the line in the footer bar.
 *
 * It replaced a floating toast. A toast covers the page it is reporting on and
 * is gone before a reader who looked away can find it; this sits where the
 * account's own controls are, and clears itself once it has been read.
 */
function setStatus(msg: string, type: 'info' | 'error' | 'success' = 'info'): void {
  if (statusTimer) clearTimeout(statusTimer);
  statusEl.textContent = msg;
  statusEl.className = `fc-account__status fc-account__status--${type}`;
  statusTimer = setTimeout(() => {
    statusEl.textContent = '';
    statusEl.className = 'fc-account__status';
    statusTimer = null;
  }, 6000);
}

async function renderDashboard(google: GoogleUser, backend: BackendUser): Promise<void> {
  currentBackendUser = backend;

  // The monogram is underneath the photo, so hiding a photo that fails to load
  // reveals an initial rather than an empty disc.
  setAvatar(userAvatar, document.getElementById('user-monogram'), google);
  userName.textContent = google.name;
  userEmail.textContent = google.email;

  const subLevel = (backend.subscriptionLevel || 'FREE').toUpperCase();
  const isFree = subLevel === 'FREE';
  const isOnHold = subLevel === 'ON_HOLD';
  const isStarter = subLevel === 'STARTER';
  const isUnlimited = subLevel === 'UNLIMITED';

  // Only show passkey for active subscribers (STARTER or UNLIMITED)
  const hasActiveSubscription = isStarter || isUnlimited;
  
  if (hasActiveSubscription) {
    currentPasskey = backend.passkey;
    passkeyVal.textContent = backend.passkey;
    passkeyBox.style.display = '';
    passkeyPrompt.style.display = 'none';
    refreshBtn.disabled = false;
  } else {
    passkeyBox.style.display = 'none';
    passkeyPrompt.style.display = '';
    refreshBtn.disabled = true;
  }

  userSince.textContent = backend.createdAt
    ? new Date(backend.createdAt).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : '-';

  // Fetch subscription details for cancellation state
  let subInfo: SubscriptionInfo | null = null;
  const token = getStoredToken();
  if (token) {
    try {
      subInfo = await getSubscription(token);
    } catch {
      // Proceed without subscription info
    }
  }

  const isCancelled = subInfo?.isCancelled ?? false;
  const accessExpiresAt = subInfo?.accessExpiresAt ?? null;

  // Subscription dropdown rendering
  renderSubscriptionDropdown(subLevel, isFree, isOnHold, isStarter, isUnlimited, isCancelled, accessExpiresAt);

  /*
   * The section sits below the branding editor and starts closed, because a
   * subscriber opens this page to work on what their viewers see. For everyone
   * else it is the page's whole point — the plans live in it, and so does the
   * "on hold" banner, which would otherwise render inside a closed box and be
   * seen by nobody. So it opens itself for an account that is not paying.
   */
  setSubscriptionOpen(!isStarter && !isUnlimited);

  // Creator branding dashboard — same gate as the passkey
  renderBrandingPanel(hasActiveSubscription, backend.channelName ?? google.name);

  showView('dashboard');
}

/**
 * Render subscription dropdown content based on current subscription state.
 * No automatic trial — users must subscribe through Paddle.
 * Paddle charges 15 days after initial subscription.
 */
function renderSubscriptionDropdown(
  subLevel: string,
  isFree: boolean,
  isOnHold: boolean,
  isStarter: boolean,
  isUnlimited: boolean,
  isCancelled: boolean,
  accessExpiresAt: string | null,
): void {
  // Format the access expiry date if available
  const expiryStr = accessExpiresAt
    ? new Date(accessExpiresAt).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : null;

  // ── Cancelled state (still on Starter or Unlimited, but cancellation pending) ──
  if (isCancelled && (isStarter || isUnlimited)) {
    // Header badge → "Unsubscribed" (red)
    currentPlanBadge.textContent = 'Unsubscribed';
    currentPlanBadge.className = 'fc-badge fc-badge--alert';

    // Account info row
    userSub.textContent = expiryStr ? `Cancelled (access until ${expiryStr})` : 'Cancelled';
    userSub.classList.add('is-alert');

    // Status banner → cancellation info
    subscriptionStatus.style.display = '';
    subscriptionStatusTitle.textContent = 'Subscription Cancelled';
    subscriptionStatusDesc.textContent = expiryStr
      ? `Your subscription has been cancelled. You retain access until ${expiryStr}.`
      : 'Your subscription has been cancelled. You retain access until the end of your billing period.';

    // Plan card badges → "Unsubscribed" on the relevant plan
    planCardStarter.classList.remove('fc-plan-card--current');
    planCardUnlimited.classList.remove('fc-plan-card--current');
    starterBadge.textContent = '';
    starterBadge.className = 'fc-badge';
    unlimitedBadge.textContent = '';
    unlimitedBadge.className = 'fc-badge';

    if (isStarter) {
      starterBadge.textContent = 'Unsubscribed';
      starterBadge.className = 'fc-badge fc-badge--alert';
    } else {
      unlimitedBadge.textContent = 'Unsubscribed';
      unlimitedBadge.className = 'fc-badge fc-badge--alert';
    }

    // Both subscribe buttons enabled for resubscribing
    checkoutStarterBtn.textContent = 'Subscribe';
    checkoutStarterBtn.disabled = false;
    checkoutUnlimitedBtn.textContent = 'Subscribe';
    checkoutUnlimitedBtn.disabled = false;

    // Hide cancel, show resubscribe
    cancelSection.style.display = 'none';
    resubscribeSection.style.display = '';

    return;
  }

  // ── Current plan badge in dropdown header ──
  if (isUnlimited) {
    currentPlanBadge.textContent = 'Unlimited';
    currentPlanBadge.className = 'fc-badge fc-badge--ok';
    userSub.textContent = 'Unlimited';
    userSub.classList.remove('is-alert');
  } else if (isStarter) {
    currentPlanBadge.textContent = 'Starter';
    currentPlanBadge.className = 'fc-badge fc-badge--ok';
    userSub.textContent = 'Starter';
    userSub.classList.remove('is-alert');
  } else if (isOnHold) {
    currentPlanBadge.textContent = 'On Hold';
    currentPlanBadge.className = 'fc-badge fc-badge--alert';
    userSub.textContent = 'On Hold';
    userSub.classList.add('is-alert');
  } else {
    currentPlanBadge.textContent = 'Free';
    currentPlanBadge.className = 'fc-badge';
    userSub.textContent = 'Free';
    userSub.classList.remove('is-alert');
  }

  // ── Status banner for inactive subscriptions ──
  if (isOnHold) {
    subscriptionStatus.style.display = '';
    subscriptionStatusTitle.textContent = 'Subscription On Hold';
    subscriptionStatusDesc.textContent =
      'Your subscription is on hold. Please renew or choose a new plan below to continue using FitCutter.';
  } else if (isFree) {
    subscriptionStatus.style.display = '';
    subscriptionStatusTitle.textContent = 'No Active Subscription';
    subscriptionStatusDesc.textContent =
      'Subscribe to a plan below to start your 15-day free trial. No charge until the trial ends.';
  } else {
    subscriptionStatus.style.display = 'none';
  }

  // ── Plan card states ──
  // Reset all cards
  planCardStarter.classList.remove('fc-plan-card--current');
  planCardUnlimited.classList.remove('fc-plan-card--current');
  starterBadge.textContent = '';
  starterBadge.className = 'fc-badge';
  unlimitedBadge.textContent = '';
  unlimitedBadge.className = 'fc-badge';

  if (isStarter) {
    planCardStarter.classList.add('fc-plan-card--current');
    starterBadge.textContent = 'Current Plan';
    starterBadge.className = 'fc-badge fc-badge--ok';
    checkoutStarterBtn.textContent = 'Current Plan';
    checkoutStarterBtn.disabled = true;

    checkoutUnlimitedBtn.textContent = 'Upgrade';
    checkoutUnlimitedBtn.disabled = false;
  } else if (isUnlimited) {
    planCardUnlimited.classList.add('fc-plan-card--current');
    unlimitedBadge.textContent = 'Current Plan';
    unlimitedBadge.className = 'fc-badge fc-badge--ok';
    checkoutUnlimitedBtn.textContent = 'Current Plan';
    checkoutUnlimitedBtn.disabled = true;

    checkoutStarterBtn.textContent = 'Downgrade';
    checkoutStarterBtn.disabled = true;
  } else {
    // FREE or ON_HOLD — both buttons are "Subscribe"
    checkoutStarterBtn.textContent = 'Subscribe';
    checkoutStarterBtn.disabled = false;
    checkoutUnlimitedBtn.textContent = 'Subscribe';
    checkoutUnlimitedBtn.disabled = false;
  }

  // ── Cancel / Resubscribe sections ──
  if (isStarter || isUnlimited) {
    cancelSection.style.display = '';
    resubscribeSection.style.display = 'none';
  } else {
    cancelSection.style.display = 'none';
    resubscribeSection.style.display = 'none';
  }
}

/** Opens or closes the subscription section, keeping its toggle's state honest. */
function setSubscriptionOpen(open: boolean): void {
  subscriptionDropdown.dataset.open = String(open);
  subscriptionToggle.setAttribute('aria-expanded', String(open));
}

/** Paints an avatar and the initial that stands in when its photo will not load. */
function setAvatar(img: HTMLImageElement, monogram: HTMLElement | null, google: GoogleUser): void {
  if (monogram) monogram.textContent = (google.name || google.email || '?').trim().charAt(0).toUpperCase();
  img.style.display = '';
  img.onerror = () => { img.style.display = 'none'; };
  img.src = google.picture;
}

function showRegisterStep(google: GoogleUser): void {
  setAvatar(registerAvatar, document.getElementById('register-monogram'), google);
  registerName.textContent = google.name;
  registerEmail.textContent = google.email;
  showView('register');
}

// ─── Auth flow ───

async function handleGoogleLogin(googleUser: GoogleUser, idToken: string): Promise<void> {
  authLoading.classList.add('show');
  authError.classList.remove('show');

  try {
    const backend = await getProfile(idToken);
    renderDashboard(googleUser, backend);
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) {
      showRegisterStep(googleUser);
    } else {
      const msg = err instanceof Error ? err.message : 'Login failed';
      authError.textContent = msg;
      authError.classList.add('show');
      showView('auth');
    }
  } finally {
    authLoading.classList.remove('show');
  }
}

async function handleYouTubeCallback(
  idToken: string,
  accessToken: string,
  googleUser: GoogleUser,
): Promise<void> {
  registerLoading.classList.add('show');
  registerError.classList.remove('show');
  showView('register');

  setAvatar(registerAvatar, document.getElementById('register-monogram'), googleUser);
  registerName.textContent = googleUser.name;
  registerEmail.textContent = googleUser.email;

  try {
    const backend = await register(idToken, accessToken);
    renderDashboard(googleUser, backend);
    setStatus('Account created.', 'success');
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Registration failed';
    registerError.textContent = msg;
    registerError.classList.add('show');
    registerLoading.classList.remove('show');
  }
}

// ─── Paddle checkout helper ───

async function openPaddleCheckout(priceId: string): Promise<void> {
  if (Config.PADDLE_DISABLED) {
    setStatus('Paddle is off in local mode. Add ?paddle=1 to the URL to enable checkout.', 'info');
    return;
  }

  const googleUser = getStoredUser();
  const email = googleUser?.email ?? currentBackendUser?.email ?? '';
  const userId = currentBackendUser?.id;
  const ok = await openCheckout(priceId, email, userId);
  if (!ok) {
    setStatus('Could not open checkout. Please refresh the page and try again.', 'error');
  }
}

// ─── Init ───

document.addEventListener('DOMContentLoaded', async () => {
  initNavbar();
  initFooterYear();
  initBrandingPanel();

  // Paddle init — runs in background, does NOT block page setup
  // SDK will be ready by the time user navigates to Subscribe
  initPaddle((event: PaddleCheckoutEvent) => {
    console.info('[Paddle] Checkout event:', event.name, event);

    if (event.name === 'checkout.completed') {
      setStatus('Payment successful — refreshing your account…', 'success');
      const token = getStoredToken();
      const user = getStoredUser();
      if (token && user) {
        setTimeout(() => handleGoogleLogin(user, token), 2000);
      }
    }

    if (event.name === 'checkout.error') {
      const errDetail = event.data?.error?.detail ?? event.data?.error?.message ?? 'Unknown error';
      console.error('[Paddle] Checkout error:', errDetail, event);
      setStatus(`Checkout error: ${errDetail}`, 'error');
    }

    if (event.name === 'checkout.warning') {
      console.warn('[Paddle] Checkout warning:', event);
    }
  });

  // ── Wire up all event listeners FIRST (before any early returns) ──

  // ── 3. Google Sign-In button ──
  initGoogleSignIn(googleBtnContainer, (user: GoogleUser, idToken: string) => {
    handleGoogleLogin(user, idToken);
  });

  // ── 4. YouTube registration button ──
  youtubeAuthBtn.addEventListener('click', () => {
    startYouTubeAuth();
  });

  // ── 5. Registration cancel ──
  registerCancelBtn.addEventListener('click', () => {
    signOut();
    showView('auth');
  });

  // ── 6. Copy passkey ──
  copyBtn.addEventListener('click', async () => {
    if (!currentPasskey) return;
    
    try {
      await navigator.clipboard.writeText(currentPasskey);
    } catch {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement('textarea');
      ta.value = currentPasskey;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    
    copyBtn.classList.add('copied');
    copyBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      Copied!
    `;
    
    setTimeout(() => {
      copyBtn.classList.remove('copied');
      copyBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        Copy
      `;
    }, 2000);
  });

  // ── 7. Refresh passkey ──
  refreshBtn.addEventListener('click', async () => {
    const token = getStoredToken();
    if (!token) return;

    refreshBtn.disabled = true;
    refreshBtn.textContent = 'Refreshing…';

    try {
      const res = await refreshPasskey(token);
      currentPasskey = res.passkey;
      passkeyVal.textContent = res.passkey;
      setStatus('Passkey refreshed successfully!', 'success');
      setStatus('Passkey updated.', 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to refresh passkey';
      setStatus(msg, 'error');
    } finally {
      refreshBtn.disabled = false;
      refreshBtn.textContent = 'Refresh Passkey';
    }
  });

  // ── 8. Sign out ──
  logoutBtn.addEventListener('click', () => {
    signOut();
    showView('auth');
  });

  // ── 9. Subscription dropdown toggle ──
  subscriptionToggle.addEventListener('click', () => {
    setSubscriptionOpen(subscriptionDropdown.dataset.open !== 'true');
  });

  // ── 10. Starter checkout ──
  checkoutStarterBtn.addEventListener('click', () => {
    openPaddleCheckout(Config.PADDLE_STARTER_PRICE_ID);
  });

  // ── 11. Unlimited checkout ──
  checkoutUnlimitedBtn.addEventListener('click', () => {
    openPaddleCheckout(Config.PADDLE_UNLIMITED_PRICE_ID);
  });

  // ── 12. Cancel subscription ──
  cancelBtn.addEventListener('click', async () => {
    const confirmed = confirm(
      'Are you sure you want to cancel your subscription?\n\n' +
      'You will retain access until the end of your current billing period plus a 10-day grace period.',
    );
    if (!confirmed) return;

    const token = getStoredToken();
    if (!token) return;

    cancelBtn.disabled = true;
    cancelBtn.textContent = 'Cancelling…';

    try {
      await cancelSubscription(token);
      setStatus('Subscription cancelled.', 'success');
      // Refresh dashboard to reflect new state
      const user = getStoredUser();
      if (user) {
        await handleGoogleLogin(user, token);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel subscription';
      setStatus(msg, 'error');
    } finally {
      cancelBtn.disabled = false;
      cancelBtn.textContent = 'Cancel Subscription';
    }
  });

  // ── 13. Resubscribe ──
  resubscribeBtn.addEventListener('click', () => {
    // Determine which plan the user was on and open checkout for it
    const subLevel = (currentBackendUser?.subscriptionLevel || 'FREE').toUpperCase();
    if (subLevel === 'UNLIMITED') {
      openPaddleCheckout(Config.PADDLE_UNLIMITED_PRICE_ID);
    } else {
      openPaddleCheckout(Config.PADDLE_STARTER_PRICE_ID);
    }
  });

  // ── 1. YouTube OAuth redirect check ──
  const ytTokens = extractYouTubeTokensFromUrl();
  if (ytTokens) {
    handleYouTubeCallback(ytTokens.idToken, ytTokens.accessToken, ytTokens.user);
    return;
  }

  // ── 2. Existing session check ──
  if (isLoggedIn()) {
    const token = getStoredToken()!;
    const user = getStoredUser()!;
    handleGoogleLogin(user, token);
  } else {
    showView('auth');
  }
});
